/* eslint-disable no-lonely-if */
/* eslint-disable dot-notation */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Stack,
  Button,
  Paper,
  Grid,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  Snackbar,
  Alert,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import UndoIcon from '@mui/icons-material/Undo';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useParams, useNavigate } from 'react-router-dom';
import '../stylemod.css';
import useResponsive from '../../hooks/useResponsive';
import logoImage from '../../media/login/logo.png';
import axios from '../../utils/axios';

const StyledRoot = styled('div')(({ theme }) => ({
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  
  // Remove padding on mobile for full screen
  [theme.breakpoints.down('sm')]: {
    padding: 0,
  },
  
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(2),
  },
}));

const SignatureContainer = styled(Paper)(({ theme }) => ({
  // Make it full height and full width on mobile
  [theme.breakpoints.down('sm')]: {
    height: '100vh',
    width: '100vw',
    maxWidth: '100vw',
    borderRadius: 0,
    padding: theme.spacing(2),
    overflowY: 'auto',
  },
  
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(3),
    borderRadius: '16px',
    maxWidth: '800px',
    width: '100%',
  },
  
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(4),
  },
  
  boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
  backgroundColor: 'white',
}));

const SignatureBox = styled('div')(({ theme }) => ({
  width: '100%',
  
  // Adjust height for mobile full screen
  [theme.breakpoints.down('sm')]: {
    height: '40vh',
    minHeight: '250px',
  },
  
  [theme.breakpoints.up('sm')]: {
    height: '250px',
  },
  
  [theme.breakpoints.up('md')]: {
    height: '300px',
  },
  
  border: '2px dashed #0072E5',
  borderRadius: '8px',
  backgroundColor: '#f8f9fa',
  cursor: 'crosshair',
  touchAction: 'none',
  position: 'relative',
  marginBottom: theme.spacing(2),
  overflow: 'hidden',
}));

// const SignatureCanvas = styled('canvas')({
//   position: 'absolute',
//   top: 0,
//   left: 0,
//   width: '100%',
//   height: '100%',
//   borderRadius: '6px',
// });
const SignatureCanvas = styled('canvas')(({ theme, disabled }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  borderRadius: '6px',
  pointerEvents: disabled ? 'none' : 'auto',
}));


// API function to check if signature already exists
const checkSignatureExistsAPI = async (db, id) => {
  try {
    const response = await axios.post(`CustomInvoice/UserSignature/Cargo/Check/${id}`,{},{
      headers: {
         'Country': db.toUpperCase(),
      },
    });
    console.log('Check signature response:', response.data.data);
    return response.data.data; // This should return "invalid", true, or false
  } catch (error) {
    // If API returns 404 or error, treat as invalid
    if (error.response && error.response.status === 404) {
      return "invalid";
    }
    throw error;
  }
};

// API function to submit signature using FormData
const submitSignatureAPI = async (db, id, signatureFile) => {
  try {
    // Create FormData
    const formData = new FormData();
    formData.append('Guid', id);
    formData.append('Signature', signatureFile, `signature_${id}.jpg`);
    
    const response = await axios.post(`CustomInvoice/UserSignature/Cargo`, formData, {
      headers: {
        'Country': db.toUpperCase(),
        'Content-Type': 'multipart/form-data',
      },
    }); 
    const data = response.data;
    return data;
  } catch (error) {
    console.error('Error submitting signature:', error);
    throw error;
  }
};

export default function UserSignature() {
  const mdUp = useResponsive('up', 'md');
  const smUp = useResponsive('up', 'sm');
  const isMobile = useResponsive('down', 'sm');
  const [open, setOpen] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [signatureExists, setSignatureExists] = useState(false);
  const [invalidUrl, setInvalidUrl] = useState(false); // New state for invalid URL
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error',
  });
  const [isDisabled, setIsDisabled] = useState(false);
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const containerRef = useRef(null); // Add ref for container
  const lastPointRef = useRef({ x: 0, y: 0 });
  const theme = useTheme();
  
  const { db, id } = useParams();
  const navigate = useNavigate();

  // Check if signature already exists on component mount
  useEffect(() => {
    const checkSignatureExists = async () => {
      try {
        if (!id) {
          setIsChecking(false);
          setInvalidUrl(true); // Set as invalid if no ID
          return;
        }
        
        const exists = await checkSignatureExistsAPI(db, id);
        console.log('Signature exists:', exists);
        
        // Handle three scenarios:
        if (exists === "invalid") {
          setInvalidUrl(true); // New scenario: invalid URL
        } else if (exists) {
          setSignatureExists(true);
          // Prevent back navigation
          window.history.pushState(null, '', window.location.href);
          window.onpopstate = function () {
            window.history.pushState(null, '', window.location.href);
          };
        } else {
          // exists = false, continue normally
          setSignatureExists(false);
        }
      } catch (error) {
        console.error('Error checking signature:', error);
        setInvalidUrl(true); // Also set invalid on error
      } finally {
        setIsChecking(false);
      }
    };

    checkSignatureExists();
  }, [db, id]);

  // Prevent back navigation after successful submission
  useEffect(() => {
    if (isSubmitted) {
      window.history.pushState(null, '', window.location.href);
      window.onpopstate = function () {
        window.history.pushState(null, '', window.location.href);
      };
    }
  }, [isSubmitted]);

  // Initialize canvas context - simplified version
  const initializeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    
    if (!canvas || !container) {
      console.log('Canvas or container not found');
      return false;
    }

    try {
      const rect = container.getBoundingClientRect();
      
      // Check if container has valid dimensions
      if (rect.width <= 0 || rect.height <= 0) {
        console.log('Container has invalid dimensions:', rect);
        return false;
      }
      
      // Set display dimensions
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      
      // Set actual canvas buffer dimensions with device pixel ratio
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        console.log('Could not get canvas context');
        return false;
      }
      
      // Scale the context to match display dimensions
      ctx.scale(dpr, dpr);
      
      // Set drawing properties
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.lineWidth = isMobile ? 3 : 2.5;
      ctx.strokeStyle = '#000000';
      ctxRef.current = ctx;

      // Clear canvas with background color
      ctx.clearRect(0, 0, rect.width, rect.height);
      ctx.fillStyle = '#f8f9fa';
      ctx.fillRect(0, 0, rect.width, rect.height);
      
      console.log('Canvas initialized successfully');
      return true;
    } catch (error) {
      console.error('Error initializing canvas:', error);
      return false;
    }
  }, [isMobile]);

  // Initialize canvas when component is ready
  useEffect(() => {
    if (!signatureExists && !isSubmitted && !invalidUrl && !isChecking && !hasDrawn) {
      const initCanvas = () => {
        const isInitialized = initializeCanvas();
        if (!isInitialized) {
          // Retry after a short delay if initialization fails
          setTimeout(initCanvas, 100);
        }
      };

      // Use a longer delay to ensure DOM is fully rendered
      const timer = setTimeout(() => {
        initCanvas();
      }, 300);

      return () => {
        clearTimeout(timer);
      };
    }
    
    return undefined;
  }, [signatureExists, isSubmitted, invalidUrl, isChecking, initializeCanvas]);

  // Handle window resize
  // useEffect(() => {
  //   const handleResize = () => {
  //     if (!signatureExists && !isSubmitted && !invalidUrl && !isChecking) {
  //       // Debounce resize handler
  //       clearTimeout(window.resizeTimer);
  //       window.resizeTimer = setTimeout(() => {
  //         initializeCanvas();
  //       }, 250);
  //     }
  //   };

  //   window.addEventListener('resize', handleResize);
    
  //   return () => {
  //     window.removeEventListener('resize', handleResize);
  //     clearTimeout(window.resizeTimer);
  //   };
  // }, [signatureExists, isSubmitted, invalidUrl, isChecking, initializeCanvas]);

  useEffect(() => {
  const handleResize = () => {
    if (!canvasRef.current || !ctxRef.current) return;

    // Save current drawing
    const dataUrl = canvasRef.current.toDataURL();

    // Re-init canvas
    initializeCanvas();

    // Restore drawing
    const img = new Image();
    img.onload = () => {
      const rect = canvasRef.current.getBoundingClientRect();
      ctxRef.current.drawImage(img, 0, 0, rect.width, rect.height);
    };
    img.src = dataUrl;
  };

  window.addEventListener('resize', handleResize);
  return () => window.removeEventListener('resize', handleResize);
}, [initializeCanvas]);


  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    
    if (canvas && ctx) {
      const container = canvas.parentElement;
      if (container) {
        const rect = container.getBoundingClientRect();
        ctx.clearRect(0, 0, rect.width, rect.height);
        ctx.fillStyle = '#f8f9fa';
        ctx.fillRect(0, 0, rect.width, rect.height);
      }
    }
    setHasDrawn(false);
    lastPointRef.current = { x: 0, y: 0 };
  };

  const getCanvasCoordinates = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    let clientX;
    let clientY;

    if (e.type.includes('touch')) {
      const touch = e.touches[0] || e.changedTouches[0];
      if (!touch) return lastPointRef.current;
      clientX = touch.clientX;
      clientY = touch.clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  };

  const startDrawing = (e) => {
    if (isDisabled) return;
    e.preventDefault();
    if (!ctxRef.current) {
      console.log('Canvas context not available');
      return;
    }
    
    const { x, y } = getCanvasCoordinates(e);
    lastPointRef.current = { x, y };
    
    ctxRef.current.beginPath();
    ctxRef.current.moveTo(x, y);
    ctxRef.current.lineTo(x, y);
    ctxRef.current.stroke();
    setIsDrawing(true);
    
    setIsSaveDisabled(false);
    setHasDrawn(true);
  };

  const draw = (e) => {
    if (isDisabled) return;
    e.preventDefault();
    if (!isDrawing || !ctxRef.current) return;
    
    const { x, y } = getCanvasCoordinates(e);
    
    ctxRef.current.beginPath();
    ctxRef.current.moveTo(lastPointRef.current.x, lastPointRef.current.y);
    ctxRef.current.lineTo(x, y);
    ctxRef.current.stroke();
    
    lastPointRef.current = { x, y };
  };

  const stopDrawing = () => {
     if (isDisabled) return;
    if (isDrawing && ctxRef.current) {
      ctxRef.current.closePath();
    }
    setIsDrawing(false);
  };

  const handleClick = (e) => {
    if (isDisabled) return;
    e.preventDefault();
    if (!ctxRef.current) return;
    
    const { x, y } = getCanvasCoordinates(e);
    
    ctxRef.current.beginPath();
    ctxRef.current.arc(x, y, ctxRef.current.lineWidth / 2, 0, Math.PI * 2);
    ctxRef.current.fillStyle = '#000000';
    ctxRef.current.fill();
    
    setIsSaveDisabled(false);
    setHasDrawn(true);
  };

  const checkCanvasHasContent = () => {
    const canvas = canvasRef.current;
    if (!canvas) return false;

    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');
    
    const rect = canvas.getBoundingClientRect();
    tempCanvas.width = rect.width;
    tempCanvas.height = rect.height;
    
    tempCtx.drawImage(canvas, 0, 0, rect.width, rect.height);
    
    const imageData = tempCtx.getImageData(0, 0, rect.width, rect.height);
    const data = imageData.data;
    
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] > 0) {
        return true;
      }
    }
    
    return false;
  };

  const clearSignature = () => {
    clearCanvas();
    setSignatureData(null);
    setIsSaveDisabled(false);
  };

  // Convert canvas to JPG and save as base64
  const saveSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (!hasDrawn || !checkCanvasHasContent()) {
      setSnackbar({
        open: true,
        message: 'Please provide your signature first.',
        severity: 'error',
      });
      return;
    }

    // Create a temporary canvas to handle DPI scaling
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    
    const rect = canvas.getBoundingClientRect();
    tempCanvas.width = rect.width;
    tempCanvas.height = rect.height;
    
    // Draw the original canvas to temp canvas, scaling down if needed
    tempCtx.drawImage(canvas, 0, 0, rect.width, rect.height);
    
    // Convert to JPG format (quality: 0.92 for good balance)
    const dataUrl = tempCanvas.toDataURL('image/jpeg', 0.92);
    setSignatureData(dataUrl);
    setIsSaveDisabled(true);
    setOpen(true);
  };

  // Handle form submission with FormData
  const handleSubmit = async () => {
    if (!signatureData) {
      setSnackbar({
        open: true,
        message: 'Please save your signature before submitting.',
        severity: 'error',
      });
      return;
    }

    if (!id) {
      setSnackbar({
        open: true,
        message: 'Document ID is missing. Please try again.',
        severity: 'error',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Convert base64 to Blob for FormData
      const base64Data = signatureData;
      const mimeType = 'image/jpeg';
      
      // Create File from base64
      const byteString = atob(base64Data.split(',')[1]);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      
      for (let i = 0; i < byteString.length; i += 1) {
        ia[i] = byteString.charCodeAt(i);
      }
      
      const blob = new Blob([ab], { type: mimeType });
      const signatureFile = new File([blob], `signature_${id}.jpg`, { type: mimeType });

      // Call API with FormData
      const result = await submitSignatureAPI(db, id, signatureFile);
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Signature submitted successfully!',
        severity: 'success',
      });
      
      // Clear and reset after a delay
      setTimeout(() => {
        clearSignature();
        setOpen(false);
        setIsSubmitted(true);
        
        // Prevent back navigation
        window.history.pushState(null, '', window.location.href);
        window.onpopstate = function () {
          window.history.pushState(null, '', window.location.href);
        };
      }, 1500);
      
    } catch (error) {
      console.error('Submission error:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to submit signature. Please try again.',
        severity: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (signatureData || hasDrawn) {
      if (window.confirm('Are you sure you want to cancel? Your signature will be lost.')) {
        clearSignature();
        navigate(-1);
      }
    } else {
      navigate(-1);
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  // Show loading while checking signature
  if (isChecking) {
    return (
      <StyledRoot>
        <Container maxWidth="sm">
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Checking signature status...
            </Typography>
          </Paper>
        </Container>
      </StyledRoot>
    );
  }

  // NEW: Show invalid URL message
  if (invalidUrl) {
    return (
      <StyledRoot>
        <Container maxWidth="sm">
          <Card sx={{ textAlign: 'center', p: 4 }}>
            <Typography variant="h4" gutterBottom color="error.main">
              Invalid URL or Document Not Found
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              The signature request URL is invalid or the document does not exist.
            </Typography>
          </Card>
        </Container>
      </StyledRoot>
    );
  }

  // Show already submitted message
  if (signatureExists) {
    return (
      <StyledRoot>
        <Container maxWidth="sm">
          <Card sx={{ textAlign: 'center', p: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom color="success.main">
              Signature Already Submitted
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Your signature has already been submitted for this document.
            </Typography>
          </Card>
        </Container>
      </StyledRoot>
    );
  }

  // Show success message after submission
  if (isSubmitted) {
    return (
      <StyledRoot>
        <Container maxWidth="sm">
          <Card sx={{ textAlign: 'center', p: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom color="success.main">
              Signature Saved Successfully!
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Your signature has been successfully submitted and saved.
            </Typography>
          </Card>
        </Container>
      </StyledRoot>
    );
  }

  // Show signature input form
  return (
    <>
      <StyledRoot>
        {isMobile ? (
          <SignatureContainer elevation={isMobile ? 0 : 3}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={2}
              mb={2}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <img
                  src={logoImage}
                  alt="Teeparam Logo"
                  style={{
                    height: '45px',
                    width: 'auto',
                    objectFit: 'contain',
                  }}
                />
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                Digital Signature
              </Typography>
            </Stack>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography 
                  variant="h6" 
                  gutterBottom 
                  align="center" 
                  color="primary"
                  sx={{ fontSize: '1.1rem' }}
                >
                  Customer Signature Required
                </Typography>
                <Typography 
                  variant="body2" 
                  paragraph 
                  align="center" 
                  color="text.secondary"
                  sx={{ fontSize: '0.8rem' }}
                >
                  {/* Please sign in the box below using your finger.
                  This signature will be used to confirm your agreement. */}
                  Please sign in the box below using finger or mouse.
                  This signature will be used for cargo custom clearance and verify the goods are sent by you.
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Box 
                sx={{ position: 'relative' }} ref={containerRef}
                
                >
                  <SignatureBox
                    onClick={handleClick}
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                    onTouchCancel={stopDrawing}
                 
                  >
                    <SignatureCanvas
                      ref={canvasRef}
                    disabled={isDisabled}
                    />
                  </SignatureBox>
                  
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      position: 'absolute',
                      bottom: 4,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      padding: '2px 8px',
                      borderRadius: '20px',
                      fontSize: '0.65rem',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    ✏️ Draw your signature here
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Stack 
                  direction="row" 
                  spacing={2} 
                  justifyContent="center"
                  alignItems="center"
                >
                  <Button
                    variant="outlined"
                    startIcon={<UndoIcon />}
                    onClick={() => {
                      clearSignature();
                      setIsDisabled(false);
                    }}
                    fullWidth
                    size="small"
                  >
                    Clear
                  </Button>
                  <Button
                    variant="contained"
                    onClick={()=>{
                      saveSignature();
                      setIsDisabled(true); // Disable further drawing after saving
                    }}
                    disabled={isSaveDisabled}
                    fullWidth
                    size="small"
                    sx={{
                      backgroundColor: '#0072E5',
                      '&:hover': {
                        backgroundColor: '#0059B2',
                      },
                      '&:disabled': {
                        backgroundColor: '#cccccc',
                        color: '#666666',
                      },
                    }}
                  >
                    {isSaveDisabled ? 'Signature Saved' : 'Save Signature'}
                  </Button>
                </Stack>
              </Grid>

              {signatureData && (
                <Grid item xs={12}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      backgroundColor: '#f8f9fa',
                    }}
                  >
                    <Typography variant="subtitle2" gutterBottom>
                      Signature Preview (JPG):
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '60px',
                      }}
                    >
                      <img
                        src={signatureData}
                        alt="Signature Preview"
                        style={{
                          maxHeight: '50px',
                          maxWidth: '150px',
                          objectFit: 'contain',
                          border: '1px solid #ddd',
                          backgroundColor: 'white',
                          padding: '6px',
                        }}
                      />
                    </Box>
                  </Paper>
                </Grid>
              )}

              <Grid item xs={12}>
                <Stack
                  direction="column"
                  spacing={1.5}
                >
                  <Button
                    variant="contained"
                    color="success"
                    size="medium"
                    onClick={handleSubmit}
                    disabled={!signatureData || isSubmitting}
                    fullWidth
                    sx={{
                      py: 1,
                      fontSize: '0.9rem',
                      backgroundColor: '#00A44B',
                      '&:hover': {
                        backgroundColor: '#007e3a',
                      },
                      '&:disabled': {
                        backgroundColor: '#cccccc',
                        color: '#666666',
                      },
                    }}
                  >
                    {isSubmitting ? (
                      <>
                        <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                        Submitting...
                      </>
                    ) : (
                      'Submit Signature'
                    )}
                  </Button>   
                  {/* <Button
                    variant="outlined"
                    color="error" 
                    size="medium"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                    fullWidth
                    sx={{
                      py: 1,
                      fontSize: '0.9rem',
                    }}
                  >
                    Cancel
                  </Button> */}
                </Stack>
              </Grid>

              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    backgroundColor: '#f8f9fa',
                    mt: 1,
                  }}
                >
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    align="center"
                    sx={{ fontSize: '0.7rem' }}
                  >
                    By submitting your signature, you agree to the terms and conditions
                    of Teeparam Services. Your electronic signature is legally binding.
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </SignatureContainer>
        ) : (
          <Container maxWidth="md">
            <SignatureContainer elevation={3}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
                mb={3}
              >
                <Stack direction="row" alignItems="center" spacing={2}>
                  <img
                    src={logoImage}
                    alt="Teeparam Logo"
                    style={{
                      height: '50px',
                      [theme.breakpoints.up('sm')]: {
                        height: '40px',
                      },
                      width: 'auto',
                      objectFit: 'contain',
                    }}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  Digital Signature
                </Typography>
              </Stack>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography 
                    variant="h5" 
                    gutterBottom 
                    align="center" 
                    color="primary"
                    sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' } }}
                  >
                    Customer Signature Required
                  </Typography>
                  <Typography 
                    variant="body1" 
                    paragraph 
                    align="center" 
                    color="text-secondary"
                  >
                    {/* Please sign in the box below using your finger or mouse.
                    This signature will be used to confirm your agreement. */}
                     Please sign in the box below using finger or mouse.
                  This signature will be used for cargo custom clearance and verify the goods are sent by you.
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Box 
                  sx={{ position: 'relative' }} ref={containerRef}
               
                  >
                    <SignatureBox
                      onClick={handleClick}
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={stopDrawing}
                      onMouseLeave={stopDrawing}
                      onTouchStart={startDrawing}
                      onTouchMove={draw}
                      onTouchEnd={stopDrawing}
                      onTouchCancel={stopDrawing}
                      
                    >
                      <SignatureCanvas
                        ref={canvasRef}
                        disabled={isDisabled}
                      />
                    </SignatureBox>
                    
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        position: 'absolute',
                        bottom: 8,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: 'rgba(255,255,255,0.9)',
                        padding: '4px 12px',
                        borderRadius: '20px',
                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                        whiteSpace: 'nowrap',
                      }}
                    >
                      ✏️ Draw your signature here
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Stack 
                    direction={{ xs: 'column', sm: 'row' }} 
                    spacing={2} 
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Button
                      variant="outlined"
                      startIcon={<UndoIcon />}
                       onClick={() => {
                      clearSignature();
                      setIsDisabled(false);
                    }}
                      fullWidth={!smUp}
                      sx={{ 
                        minWidth: { xs: '100%', sm: '120px' },
                        py: { xs: 1, sm: 0.5 }
                      }}
                    >
                      Clear
                    </Button>
                    <Button
                      variant="contained"
                     onClick={()=>{
                      saveSignature();
                      setIsDisabled(true); // Disable further drawing after saving
                    }}
                      disabled={isSaveDisabled}
                      fullWidth={!smUp}
                      sx={{
                        minWidth: { xs: '100%', sm: '140px' },
                        py: { xs: 1, sm: 0.5 },
                        backgroundColor: '#0072E5',
                        '&:hover': {
                          backgroundColor: '#0059B2',
                        },
                        '&:disabled': {
                          backgroundColor: '#cccccc',
                          color: '#666666',
                        },
                      }}
                    >
                      {isSaveDisabled ? 'Signature Saved' : 'Save Signature'}
                    </Button>
                  </Stack>
                </Grid>

                {signatureData && (
                  <Grid item xs={12}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        backgroundColor: '#f8f9fa',
                      }}
                    >
                      <Typography variant="subtitle1" gutterBottom>
                        Signature Preview (JPG):
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          minHeight: '80px',
                        }}
                      >
                        <img
                          src={signatureData}
                          alt="Signature Preview"
                          style={{
                            maxHeight: '60px',
                            maxWidth: '180px',
                            objectFit: 'contain',
                            border: '1px solid #ddd',
                            backgroundColor: 'white',
                            padding: '8px',
                          }}
                        />
                      </Box>
                    </Paper>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Button
                      variant="contained"
                      color="success"
                      size="large"
                      onClick={handleSubmit}
                      disabled={!signatureData || isSubmitting}
                      fullWidth={!smUp}
                      sx={{
                        minWidth: { xs: '100%', sm: '200px', md: '220px' },
                        py: { xs: 1.5, sm: 1 },
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                        backgroundColor: '#00A44B',
                        '&:hover': {
                          backgroundColor: '#007e3a',
                        },
                        '&:disabled': {
                          backgroundColor: '#cccccc',
                          color: '#666666',
                        },
                      }}
                    >
                      {isSubmitting ? (
                        <>
                          <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                          Submitting...
                        </>
                      ) : (
                        'Submit Signature'
                      )}
                    </Button>
                    {/* <Button
                      variant="outlined"
                      color="error"
                      size="large"
                      onClick={handleCancel}
                      disabled={isSubmitting}
                      fullWidth={!smUp}
                      sx={{
                        minWidth: { xs: '100%', sm: '200px', md: '220px' },
                        py: { xs: 1.5, sm: 1 },
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                      }}
                    >
                      Cancel
                    </Button> */}
                  </Stack>
                </Grid>

                <Grid item xs={12}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      backgroundColor: '#f8f9fa',
                      mt: 2,
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      align="center"
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    >
                      By submitting your signature, you agree to the terms and conditions
                      of Teeparam Services. Your electronic signature is legally binding.
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </SignatureContainer>
          </Container>
        )}
      </StyledRoot>

      <Dialog 
        open={open} 
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Signature Saved</DialogTitle>
        <DialogContent>
          <Typography>
            Your signature has been saved as JPG. Click "Submit Signature" to finalize.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}